// src/components/settings/ChecklistSettings.js
// CHECKLISTS SEAM 6 VERIFIER: chklstbuild-6c1e9b42
//
// Settings › Checklists. The company's checklist templates: list, new, edit, duplicate,
// archive / restore. Owner / Admin / Manager only (SettingsPage canBuildChecklists, the
// server's BUILDER_ROLES), and only where the roll-out switch is on (checklistTemplateDraft).
//
// Templates live in organizations/{orgId}/checklistTemplates. firestore.rules let staff READ
// them and deny every client write, so every change goes through saveChecklistTemplate /
// setChecklistTemplateArchived (functions/utils/checklists.js), which re-check the role.
//
// The editor opens IN PLACE of the list (still inside Settings, not a modal). This component
// owns which one shows; the editor asks before leaving with unsaved changes.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { ClipboardCheck, Lock, Plus, Pencil, Copy, Archive, RotateCcw, AlertTriangle } from 'lucide-react';
import { db } from '../../firebase/config';
import { setChecklistTemplateArchived } from '../../utils/checklistClient';
import { CHECKLIST_LIMITS, emptyDraft, duplicateDraft, questionSummary } from '../../utils/checklistTemplateDraft';
import ChecklistTemplateEditor from './ChecklistTemplateEditor';

const PILL = 'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full whitespace-nowrap';

export default function ChecklistSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;
    const [templates, setTemplates] = useState(null); // null = loading
    const [loadError, setLoadError] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [busy, setBusy] = useState({});      // { [templateId]: 'Archiving…' | 'Restoring…' }
    const [rowError, setRowError] = useState({}); // { [templateId]: message }
    const [view, setView] = useState({ mode: 'list' }); // or { mode: 'edit', templateId, seedDraft, seedDirty, key }

    useEffect(() => {
        if (!orgId) return undefined;
        return onSnapshot(collection(db, `organizations/${orgId}/checklistTemplates`), (snap) => {
            setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setLoadError('');
        }, (err) => {
            console.error('ChecklistSettings: could not load checklists', err);
            setLoadError('Could not load your checklists. Please refresh the page.');
            setTemplates([]);
        });
    }, [orgId]);

    const sorted = useMemo(() => [...(templates || [])].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })), [templates]);
    const archivedCount = sorted.filter((t) => t.archived === true).length;
    const visible = sorted.filter((t) => showArchived || t.archived !== true);
    const atCap = (templates || []).length >= CHECKLIST_LIMITS.templatesPerOrg;

    // seedDirty: a duplicate counts as unsaved from the start (leaving asks first); a blank new one doesn't.
    const openNew = () => setView({ mode: 'edit', templateId: null, seedDraft: emptyDraft(), seedDirty: false, key: Date.now() });
    const openEdit = (t) => setView({ mode: 'edit', templateId: t.id, seedDraft: null, seedDirty: false, key: Date.now() });
    const openDuplicate = (t) => setView({ mode: 'edit', templateId: null, seedDraft: duplicateDraft(t), seedDirty: true, key: Date.now() });

    const setArchived = async (t, archived) => {
        setBusy((b) => ({ ...b, [t.id]: archived ? 'Archiving…' : 'Restoring…' }));
        setRowError((e) => ({ ...e, [t.id]: '' }));
        try {
            await setChecklistTemplateArchived({ templateId: t.id, archived });
        } catch (err) {
            console.error('ChecklistSettings: archive failed', err);
            setRowError((e) => ({ ...e, [t.id]: err?.message || 'Could not change this checklist. Please try again.' }));
        } finally {
            setBusy((b) => { const n = { ...b }; delete n[t.id]; return n; });
        }
    };

    if (!orgId) return null;

    if (view.mode === 'edit') {
        return (
            <ChecklistTemplateEditor
                key={view.key}
                orgId={orgId}
                templateId={view.templateId}
                seedDraft={view.seedDraft}
                seedDirty={view.seedDirty}
                onExit={() => setView({ mode: 'list' })}
            />
        );
    }

    const newButton = (
        <button type="button" onClick={openNew} disabled={atCap}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#5F4EC9] rounded-lg hover:opacity-90 disabled:opacity-50">
            <Plus size={16} /> New checklist
        </button>
    );

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-2xl">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <ClipboardCheck size={18} className="text-gray-500" /> Checklists
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Build the forms your team fills out on jobs. Techs fill them in on the Spatchy app — only the office can create or change them.
                    </p>
                </div>
                {templates !== null && templates.length > 0 && newButton}
            </div>

            {atCap && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <p>Your company has {CHECKLIST_LIMITS.templatesPerOrg} checklists (archived ones count), which is the most it can have. Archiving doesn't free a slot.</p>
                </div>
            )}

            {loadError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" /><p>{loadError}</p>
                </div>
            )}

            {templates === null ? (
                <div className="text-sm text-gray-400">Loading…</div>
            ) : templates.length === 0 && !loadError ? (
                <div className="p-8 text-center border border-dashed border-gray-300 rounded-xl space-y-3">
                    <ClipboardCheck size={28} className="mx-auto text-gray-300" />
                    <p className="text-sm text-gray-600 max-w-md mx-auto">
                        Checklists are forms your team fills out on a job, like a safety check, an install record or a customer sign-off.
                        Build one here and add it to jobs, or have it added to every new job automatically.
                    </p>
                    <div className="flex justify-center">{newButton}</div>
                </div>
            ) : (
                <>
                    <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <div className="col-span-4">Name</div>
                            <div className="col-span-2">Goes on</div>
                            <div className="col-span-3">Completing the job</div>
                            <div className="col-span-3 text-right">Actions</div>
                        </div>
                        {visible.length === 0 && (
                            <div className="px-4 py-6 text-sm text-gray-500 text-center">All of your checklists are archived. Turn on "Show archived checklists" to see them.</div>
                        )}
                        {visible.map((t) => {
                            const archived = t.archived === true;
                            const rowBusy = busy[t.id];
                            return (
                                <div key={t.id} className={`grid grid-cols-12 gap-3 items-center px-4 py-3 ${archived ? 'bg-gray-50' : ''}`}>
                                    <div className="col-span-12 md:col-span-4 min-w-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={`font-medium truncate ${archived ? 'text-gray-500' : 'text-gray-800'}`}>{t.name || 'Untitled checklist'}</span>
                                            {archived && <span className={`${PILL} bg-gray-100 text-gray-600 border-gray-300`}>Archived</span>}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">{questionSummary(t.sections)}</p>
                                    </div>
                                    <div className="col-span-6 md:col-span-2">
                                        {t.autoAttach === 'every_job'
                                            ? <span className={`${PILL} bg-[#5F4EC9]/10 text-[#5F4EC9] border-[#5F4EC9]/30`}>Every new job</span>
                                            : <span className={`${PILL} bg-gray-50 text-gray-600 border-gray-200`}>Added by hand</span>}
                                    </div>
                                    <div className="col-span-6 md:col-span-3">
                                        {t.blocksCompletion === true
                                            ? <span className={`${PILL} bg-amber-50 text-amber-800 border-amber-300`}><Lock size={11} /> Must be finished first</span>
                                            : <span className="text-xs text-gray-500">Optional</span>}
                                    </div>
                                    <div className="col-span-12 md:col-span-3 flex flex-wrap items-center md:justify-end gap-1">
                                        {rowBusy ? (
                                            <span className="text-sm text-gray-500">{rowBusy}</span>
                                        ) : (
                                            <>
                                                {!archived && (
                                                    <button type="button" onClick={() => openEdit(t)}
                                                        className="flex items-center gap-1 px-2 py-1 text-sm text-gray-700 rounded hover:bg-gray-100">
                                                        <Pencil size={13} /> Edit
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => openDuplicate(t)} disabled={atCap}
                                                    title={atCap ? 'Your company already has the most checklists it can have.' : undefined}
                                                    className="flex items-center gap-1 px-2 py-1 text-sm text-gray-700 rounded hover:bg-gray-100 disabled:opacity-40">
                                                    <Copy size={13} /> Duplicate
                                                </button>
                                                {archived ? (
                                                    <button type="button" onClick={() => setArchived(t, false)}
                                                        className="flex items-center gap-1 px-2 py-1 text-sm text-gray-700 rounded hover:bg-gray-100">
                                                        <RotateCcw size={13} /> Restore
                                                    </button>
                                                ) : (
                                                    <button type="button" onClick={() => setArchived(t, true)}
                                                        className="flex items-center gap-1 px-2 py-1 text-sm text-gray-700 rounded hover:bg-gray-100">
                                                        <Archive size={13} /> Archive
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {rowError[t.id] && (
                                        <p className="col-span-12 text-sm text-red-700">{rowError[t.id]}</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {archivedCount > 0 && (
                        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                                className="rounded border-gray-300" />
                            Show archived checklists ({archivedCount})
                        </label>
                    )}
                    <p className="text-xs text-gray-400 -mt-3">
                        Archived checklists can't be added to jobs and stop being added to new jobs. Jobs that already have one keep it.
                    </p>
                </>
            )}
        </div>
    );
}
